/// <reference types="@types/googlemaps" />
/*global google*/

import React, { useState, useRef, useEffect } from "react";
import { connect } from "react-redux";
import { compose } from "recompose";
import {
  setExploreActiveDrawingObject,
  updateExploreObjectById,
  setExploreGeoFilter,
  setRadius,
  appNotify
} from 'store/actions';
import { DrawingManager } from '@react-google-maps/api';
import { Constants } from 'utilities/constants';
import { viewObjects } from 'store/explore/views';
import { formatValue } from "utilities/format";

export function reconstructDataForObject(objectType, overlayType, obj) {
  switch (overlayType) {
    case 'polygon':
    case 'polyline':
      if (objectType === Constants.ViewsObjectType.PROPERTYDATA) {
        return { filterGeo: [...obj.filterGeo] };
      } else {
        return { data: [...obj.data] }
      }
    case 'circle':
        return { data: { position: { ...obj.data.position }, radius: obj.data.radius }}
    case 'marker':
      return { position: { ...obj.position }};
    case 'Distance Measuring':
      return { data: {
        ...obj.data,
        coordinates: [...obj.data.coordinates],
        totalData: { ...obj.data.totalData }
      } }
    default: return null;
  }
}

export function getGeoFromOverlay(objectType, e) {
  if (e.type === 'polygon' || e.type === 'polyline') {
    if (e.overlay && e.overlay.latLngs && e.overlay.latLngs.length === 1) {
      const vertices = e.overlay.getPath();
      if (vertices && vertices.getLength() > 0) {
        var points = [];
        for (var i = 0; i < vertices.getLength(); i++) {
          var xy = vertices.getAt(i);
          const lat = xy.lat();
          const lng = xy.lng();
          points.push({lat,lng});
        }
        if (e.type === 'polygon') {
          const firstPoint = points[0];
          const lastPoint = points[points.length - 1];
          if (firstPoint.lat !== lastPoint.lat || firstPoint.lng !== lastPoint.lng) {
            points.push(points[0]); //close out polygon with inital point
          }
        }
        // let data = JSON.stringify(points);
        // if (data.length > 1800) { //limit how long/details the polygon can be, since we send the bounds thru query string
        //   alert('Custom object is too long. Try again with a less detailed shape.');
        //   return null;
        // } else {
        if (objectType === Constants.ViewsObjectType.PROPERTYDATA) {
          const area = google.maps.geometry.spherical.computeArea(vertices); // Unit = square meters
          console.log('Shape area: ' + area)
          return {
            filterGeo: points,
            isOversize: area > Constants.ViewsPropertyDataGeoFilterAreaLimit
          }
        }
        const field = objectType === Constants.ViewsObjectType.PROPERTYDATA ? 'filterGeo' : 'data'; // Handling for geo filter polygons drawn in Property Data
        return { [field]: points };
        // }
      }
    }
  } else if (e.type === 'marker') {
    const position = e.overlay.getPosition();
    const point = {
      lat: position.lat(),
      lng: position.lng()
    }
    return { position: point }
  } else if (e.type === 'circle') {
    const position = e.overlay.getCenter();
    const bounds = e.overlay.getBounds();
    const point = {
      lat: position.lat(),
      lng: position.lng()
    }
    const radius = e.overlay.getRadius();

    return { data: { position: point, radius, bounds }};
  }
  return null;
}

function getPolygonOptions(mapType, editable = true) {
  const mapViewPolygonOptions = {
    editable: true,
    draggable: true,
    fillColor: `#4286f4`,
    fillOpacity: 0.15,
    strokeWeight: 3,
    strokeColor: `#4286f4`,
    zIndex: 1000
  }
  const satelliteViewPolygonOptions = {
    editable: true,
    draggable: true,
    fillColor: `#ffff00`,
    fillOpacity: 0.15,
    strokeWeight: 3,
    strokeColor: `#ffff00`,
    zIndex: 1000
  }
  return mapType === 'satellite' || mapType === 'hybrid' ? satelliteViewPolygonOptions : mapViewPolygonOptions;
}
const iconPath = 'M0-48c-9.8 0-17.7 7.8-17.7 17.4 0 15.5 17.7 30.6 17.7 30.6s17.7-15.4 17.7-30.6c0-9.6-7.9-17.4-17.7-17.4z';
const circlePath = ' M-6,-30a6,6 0 1,0 12,0a6,6 0 1,0 -12,0';

function MapDrawingManager({
  mapReady,
  mapType,
  activeDrawingObject,
  setExploreActiveDrawingObject,
  updateExploreObjectById,
  setExploreGeoFilter,
  setRadius,
  manualRadius,
  appNotify
}) {
  const drawingManagerRef = useRef(null);
  const [polygons, setPolygons] = useState({});

  const drawingManagerOptions = useRef({
    drawingControl: false,
    drawingMode: null,
    markerOptions: {
      clickable: false,
      draggable: true,
      icon: {
        path: iconPath + circlePath,
        fillColor: '#ffa300',
        fillOpacity: 1,
        anchor: new window.google.maps.Point(0, 0),
        strokeWeight: 3.0,
        strokeColor: '#fff',
        scale: 0.9
      }
    },
    polylineOptions: {
      editable: true,
      draggable: true,
      strokeColor: '#ffa300',
      strokeWeight: 5,
      zIndex: 1000
    },
    polygonOptions: getPolygonOptions(mapType),
    circleOptions: {
      fillColor: '#ffa300',
      strokeColor: '#ffa300',
      editable: true,
      draggable: true,
      radius_changed: radiusHandler,
    }
  });

  function radiusHandler() {
    setRadius(this.getRadius());
  }

  function getActiveRef() {
    const { id } = activeDrawingObject || {};
    return polygons[id];
  }

  useEffect(() => {
    const activeRef = getActiveRef();
    if (activeRef) {
      activeRef.overlay.setRadius(manualRadius);
    }
  }, [manualRadius])

  const drawingObjectType = activeDrawingObject?.type;
  const drawingMode = drawingObjectType ? viewObjects[drawingObjectType].drawingMode : null;
  useEffect(() => {
    if (!drawingManagerRef.current) return;

    drawingManagerRef.current.setDrawingMode(drawingMode);

    if (!drawingObjectType) {
      Object.keys(polygons).forEach(id => {
        polygons[id].overlay.setMap(null);
      });
      setPolygons({});
    }
  }, [drawingObjectType]);

  useEffect(() => {
    const drawingType =
      activeDrawingObject?.type !== 'DistanceMeasuring'
      && activeDrawingObject?.type !== 'Label';

    if (activeDrawingObject?.isCreating && drawingType) {
      const { id, type } = activeDrawingObject;
      const activeRef = polygons[id];
      const geo = getGeoFromOverlay(type, activeRef);
      if (geo) {
        if (geo.filterGeo) {
          if (geo.isOversize) {
            const SqMetersToSqMiles = (squareMeters) => {
              const sqMiles = squareMeters * 0.00000038610215855;
              return formatValue(sqMiles, { type: 'fixed', decimals: 0, useSeparator: true })
            }
            appNotify(`Area exceeds limit of ${SqMetersToSqMiles(Constants.ViewsPropertyDataGeoFilterAreaLimit)} square miles. Please lower the shape size and try again.`, 'error');
            return;
          }
          setExploreGeoFilter(id, geo.filterGeo);
        } else {
          updateExploreObjectById(id, geo);
        }
      }
      setExploreActiveDrawingObject(null);
    }
  }, [activeDrawingObject])

  function onOverlayComplete(e, id) {
    const activeId = id ?? activeDrawingObject?.id;
    if ((e.type === 'polygon' || e.type === 'polyline') && e.overlay.getPath()?.getLength() > 500) { // Limit length of overlay
      alert('Number of points drawn is too long. Try again with a less detailed shape.');
      e.overlay.setMap(null);
      return;
    }
    drawingManagerRef.current.setDrawingMode(null);
    if (activeId == null) { // Handling for clearing an object mid-draw, prior to initially creating it
      e.overlay.setMap(null);
      return;
    };
    setPolygons({ ...polygons, [activeId]: e });
    setExploreActiveDrawingObject({
      ...activeDrawingObject,
      isPlaced: true
    });
  }

  const onLoad = ref => {
    drawingManagerRef.current = ref;
  }

  if (!mapReady || drawingObjectType === 'DistanceMeasuring') return null;
  return (
    <DrawingManager
      onLoad={onLoad}
      options={drawingManagerOptions.current}
      onOverlayComplete={onOverlayComplete}

    />
  )
}

function mapState({ exploreShapesData, exploreMap, exploreViews }) {
  return { ...exploreShapesData,
    mapReady: exploreMap.bounds !== null && exploreMap.bounds !== undefined,
    mapType: exploreMap.mapType,
    activeDrawingObject: exploreViews.activeDrawingObject,
    manualRadius: exploreViews.manualRadius,
  };
}

const mapDispatch = {
  setExploreActiveDrawingObject,
  updateExploreObjectById,
  setExploreGeoFilter,
  setRadius,
  appNotify
}

export default compose(
  connect(mapState, mapDispatch)
)(MapDrawingManager);

